/**
 * Foremann Voice Assistant Component
 *
 * A floating voice assistant that allows hands-free order management.
 * Uses Web Speech API for voice recognition and Gemini for NLU.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Mic,
  MicOff,
  X,
  MessageSquare,
  Volume2,
  VolumeX,
  Loader2,
  Bot,
  User,
  Minimize2,
  Maximize2,
  AlertCircle,
} from 'lucide-react';
import { foremannAI, type ForemannState } from '../services/foremannAI';
import { orderApi } from '../services/api';
import type { Order } from '../types';

/** Conversation message bubble */
function MessageBubble({ msg }: { msg: { role: string; content: string } }) {
  return (
    <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {msg.role === 'assistant' && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
          <Bot className="h-4 w-4 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
          msg.role === 'user'
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-800 rounded-bl-md'
        }`}
      >
        <p className="text-sm">{msg.content}</p>
      </div>
      {msg.role === 'user' && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
          <User className="h-4 w-4 text-gray-600" />
        </div>
      )}
    </div>
  );
}

/** Pending order preview panel */
function PendingOrderPreview({ pendingOrder }: { pendingOrder: Record<string, string | undefined> }) {
  if (Object.keys(pendingOrder).length === 0) return null;
  return (
    <div className="mx-4 mb-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex items-center gap-2 text-blue-800 text-sm font-medium mb-2">
        <MessageSquare className="h-4 w-4" />
        Pöntun í vinnslu
      </div>
      <div className="text-xs text-blue-700 space-y-1">
        {pendingOrder.type && <p>Tegund: {pendingOrder.type}</p>}
        {pendingOrder.customer_name && <p>Viðskiptavinur: {pendingOrder.customer_name}</p>}
        {pendingOrder.scheduled_date && <p>Dagsetning: {pendingOrder.scheduled_date}</p>}
      </div>
    </div>
  );
}

/** Status indicators (processing, speaking) */
function StatusIndicators({ isProcessing, isSpeaking }: { isProcessing: boolean; isSpeaking: boolean }) {
  return (
    <>
      {isProcessing && (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Vinnur...</span>
        </div>
      )}
      {isSpeaking && (
        <div className="flex items-center gap-2 text-purple-600">
          <Volume2 className="h-4 w-4 animate-pulse" />
          <span className="text-sm">Talar...</span>
        </div>
      )}
    </>
  );
}

/** Try to execute a confirmed order, return the created order or null */
async function tryExecuteOrder(
  pendingOrder: Record<string, string | undefined>,
): Promise<Order | null> {
  if (!pendingOrder.customer_id) return null;
  const result = await orderApi.create({
    customer_id: pendingOrder.customer_id,
    container_id: pendingOrder.container_id,
    type: (pendingOrder.type || 'pickup') as Order['type'],
    scheduled_date: pendingOrder.scheduled_date,
    notes: pendingOrder.notes,
    status: 'pending',
  });
  return result.data;
}

/** Assistant header bar with title and controls */
function AssistantHeader({
  isListening, isMuted, isMinimized,
  onToggleMute, onToggleMinimize, onClose,
}: {
  isListening: boolean; isMuted: boolean; isMinimized: boolean;
  onToggleMute: () => void; onToggleMinimize: () => void; onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl">
      <div className="flex items-center gap-2 text-white">
        <Bot className="h-5 w-5" />
        <span className="font-semibold">Foremann</span>
        {isListening && (
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Hlustandi...</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onToggleMute} className="p-1.5 rounded-lg hover:bg-white/20 text-white"
          title={isMuted ? 'Kveikja á hljóði' : 'Slökkva á hljóði'}>
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
        <button onClick={onToggleMinimize} className="p-1.5 rounded-lg hover:bg-white/20 text-white">
          {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
        </button>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Voice and text input area */
function InputArea({
  isListening, isProcessing, inputText,
  onVoiceToggle, onTextChange, onTextSubmit, onQuickCommand, onClear,
}: {
  isListening: boolean; isProcessing: boolean; inputText: string;
  onVoiceToggle: () => void; onTextChange: (v: string) => void;
  onTextSubmit: (e: React.FormEvent) => void;
  onQuickCommand: (cmd: string) => void; onClear: () => void;
}) {
  const voiceClass = isListening
    ? 'bg-red-500 text-white animate-pulse'
    : 'bg-blue-600 text-white hover:bg-blue-700';
  return (
    <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
      <div className="flex items-center gap-2">
        <button onClick={onVoiceToggle} disabled={isProcessing}
          className={`flex-shrink-0 p-3 rounded-full transition-all ${voiceClass} disabled:opacity-50`}
          title={isListening ? 'Stöðva' : 'Tala'}>
          {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        <form onSubmit={onTextSubmit} className="flex-1 flex gap-2">
          <input type="text" value={inputText} onChange={(e) => onTextChange(e.target.value)}
            placeholder="Eða skrifaðu hér..."
            className="flex-1 px-4 py-2 rounded-full border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            disabled={isProcessing || isListening} />
          <button type="submit" disabled={!inputText.trim() || isProcessing}
            className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Senda
          </button>
        </form>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex gap-1">
          {(['tæming', 'afhending', 'hjálp'] as const).map((cmd) => (
            <button key={cmd} onClick={() => onQuickCommand(cmd)} disabled={isProcessing}
              className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-full capitalize">
              {cmd.charAt(0).toUpperCase() + cmd.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700">Hreinsa</button>
      </div>
    </div>
  );
}

interface ForemannVoiceAssistantProps {
  onOrderCreated?: (order: Order) => void;
}

export function ForemannVoiceAssistant({ onOrderCreated }: ForemannVoiceAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [state, setState] = useState<ForemannState>({
    isListening: false,
    isProcessing: false,
    isSpeaking: false,
    currentIntent: null,
    pendingOrder: {},
    conversationHistory: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [inputText, setInputText] = useState('');

  useEffect(() => {
    // Set up state callback
    foremannAI.setStateCallback((newState) => {
      setState((prev) => ({ ...prev, ...newState }));
    });
  }, []);

  const handleVoiceInput = useCallback(async () => {
    setError(null);
    try {
      const text = await foremannAI.startListening();
      if (text) {
        await processCommand(text);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við raddgreiningnu');
    }
  }, []);

  const processCommand = async (text: string) => {
    try {
      const result = await foremannAI.processCommand(text);

      // Handle confirmed order execution
      if (result.action === 'execute' && result.entities?.confirmation === 'yes') {
        const created = await tryExecuteOrder(state.pendingOrder);
        if (created) {
          onOrderCreated?.(created);
          setState((prev) => ({ ...prev, pendingOrder: {} }));
        }
      }

      // Update pending order with extracted entities
      if (result.intent === 'create_order' && result.entities) {
        setState((prev) => ({
          ...prev,
          pendingOrder: { ...prev.pendingOrder, ...result.entities },
        }));
      }

      // Speak response if not muted
      if (!isMuted && result.response) {
        await foremannAI.speak(result.response);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við vinnslu');
    }
  };

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim()) {
      await processCommand(inputText);
      setInputText('');
    }
  };

  const stopListening = () => {
    foremannAI.stopListening();
  };

  const clearHistory = () => {
    foremannAI.clearHistory();
    setState((prev) => ({ ...prev, conversationHistory: [], pendingOrder: {} }));
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
        title="Foremann AI Aðstoðarmaður"
      >
        <Bot className="h-7 w-7" />
      </button>
    );
  }

  const containerClass = isMinimized
    ? 'bottom-6 right-6 w-80 h-16'
    : 'bottom-6 right-6 w-96 max-h-[600px] flex flex-col';
  const hasHistory = state.conversationHistory && state.conversationHistory.length > 0;
  const voiceToggle = state.isListening ? stopListening : handleVoiceInput;

  return (
    <div className={`fixed z-50 bg-white rounded-2xl shadow-2xl transition-all ${containerClass}`}>
      <AssistantHeader
        isListening={state.isListening} isMuted={isMuted} isMinimized={isMinimized}
        onToggleMute={() => setIsMuted(!isMuted)}
        onToggleMinimize={() => setIsMinimized(!isMinimized)}
        onClose={() => setIsOpen(false)}
      />

      {!isMinimized && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[350px]">
            {hasHistory ? (
              (state.conversationHistory || []).map((msg, idx) => (
                <MessageBubble key={idx} msg={msg} />
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Bot className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Hæ! Ég er Foremann.</p>
                <p className="text-sm mt-1">Segðu &quot;tæming&quot; til að stofna pöntun</p>
                <p className="text-xs mt-2 text-gray-400">Eða ýttu á hljóðnemann til að tala</p>
              </div>
            )}
            <StatusIndicators isProcessing={state.isProcessing} isSpeaking={state.isSpeaking} />
          </div>

          <PendingOrderPreview pendingOrder={state.pendingOrder} />

          {error && (
            <div className="mx-4 mb-2 p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <InputArea
            isListening={state.isListening} isProcessing={state.isProcessing} inputText={inputText}
            onVoiceToggle={voiceToggle} onTextChange={setInputText}
            onTextSubmit={handleTextSubmit} onQuickCommand={processCommand} onClear={clearHistory}
          />
        </>
      )}
    </div>
  );
}

export default ForemannVoiceAssistant;
